/**
 * Wraps a lazy `import()` loader with a single delayed retry, to ride out a
 * transient dynamic-import failure (a brief network blip or CDN propagation lag)
 * before the global chunk-recovery reload takes over. It does NOT help when a
 * chunk is genuinely gone after a stale deploy — that case is handled by
 * {@link ChunkRecoveryService} (reload) — so keep the retry count low.
 * @param loader the dynamic-import factory to invoke
 * @param retries extra attempts after the first (default 1)
 * @param delayMs delay between attempts, in milliseconds (default 400)
 * @returns a promise resolving to the loaded module
 */
export function lazyRetry<T>(loader: () => Promise<T>, retries = 1, delayMs = 400): Promise<T> {
  return loader().catch((error: unknown) => {
    if (retries <= 0) {
      throw error;
    }
    return new Promise<void>(resolve => setTimeout(resolve, delayMs)).then(() =>
      lazyRetry(loader, retries - 1, delayMs),
    );
  });
}
